import { useCallback, useEffect, useRef, useState } from "react"
import { getWsBaseUrl } from "../api/baseUrl"
import type {
  ActionCommand,
  MatchSeatConnectionState,
  MatchSeatDisconnectDeadlines,
  MatchState,
} from "../schemas"
import type { MatchSocketError } from "./errorMessages"

type ServerMessage =
  | { type: "state"; state: MatchState }
  | {
      type: "presence"
      connectionState: MatchSeatConnectionState
      disconnectDeadlines: MatchSeatDisconnectDeadlines
    }
  | { type: "error"; code?: string; error: string }

export type WsStatus = "connecting" | "open" | "closed" | "error"

const MAX_RECONNECT_ATTEMPTS = 5
const RECONNECT_DELAYS_MS = [800, 1_500, 2_500, 4_000, 6_000] as const

export function useMatchWebSocket(
  matchId: string | null,
  seatId: string | null,
  retryKey = 0,
): {
  connectionState: MatchSeatConnectionState | null
  disconnectDeadlines: MatchSeatDisconnectDeadlines | null
  state: MatchState | null
  status: WsStatus
  wsError: MatchSocketError | null
  sendCommand: (cmd: ActionCommand) => void
} {
  const [connectionState, setConnectionState] =
    useState<MatchSeatConnectionState | null>(null)
  const [disconnectDeadlines, setDisconnectDeadlines] =
    useState<MatchSeatDisconnectDeadlines | null>(null)
  const [state, setState] = useState<MatchState | null>(null)
  const [status, setStatus] = useState<WsStatus>("connecting")
  const [wsError, setWsError] = useState<MatchSocketError | null>(null)
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimerRef = useRef<number | null>(null)
  const reconnectAttemptRef = useRef(0)
  const closedByCleanupRef = useRef(false)
  const connectionKeyRef = useRef<string | null>(null)

  useEffect(() => {
    const nextConnectionKey = matchId && seatId ? `${matchId}:${seatId}` : null
    const connectionChanged = connectionKeyRef.current !== nextConnectionKey
    connectionKeyRef.current = nextConnectionKey

    if (reconnectTimerRef.current !== null) {
      window.clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = null
    }

    if (!matchId || !seatId) {
      closedByCleanupRef.current = true
      wsRef.current?.close()
      wsRef.current = null
      reconnectAttemptRef.current = 0
      setConnectionState(null)
      setDisconnectDeadlines(null)
      setStatus("closed")
      setWsError(null)
      setState(null)
      return
    }

    if (connectionChanged) {
      setConnectionState(null)
      setDisconnectDeadlines(null)
      reconnectAttemptRef.current = 0
      setState(null)
    }

    let cancelled = false

    const connect = () => {
      if (cancelled) return

      if (reconnectTimerRef.current !== null) {
        window.clearTimeout(reconnectTimerRef.current)
        reconnectTimerRef.current = null
      }

      closedByCleanupRef.current = false
      setStatus(reconnectAttemptRef.current > 0 ? "connecting" : "connecting")
      setWsError(null)

      const url = `${getWsBaseUrl()}/api/match/${matchId}/ws?seat=${seatId}&attempt=${retryKey}&retry=${reconnectAttemptRef.current}`
      const ws = new WebSocket(url)
      wsRef.current = ws

      ws.addEventListener("open", () => {
        if (cancelled || wsRef.current !== ws) return
        reconnectAttemptRef.current = 0
        setStatus("open")
        setWsError(null)
      })

      ws.addEventListener("close", () => {
        if (wsRef.current === ws) {
          wsRef.current = null
        }
        if (cancelled || closedByCleanupRef.current) {
          setStatus("closed")
          return
        }

        if (reconnectAttemptRef.current < MAX_RECONNECT_ATTEMPTS) {
          const delay =
            RECONNECT_DELAYS_MS[
              Math.min(
                reconnectAttemptRef.current,
                RECONNECT_DELAYS_MS.length - 1,
              )
            ]
          reconnectAttemptRef.current += 1
          setStatus("connecting")
          reconnectTimerRef.current = window.setTimeout(connect, delay)
          return
        }

        setStatus("closed")
      })

      ws.addEventListener("error", () => {
        if (cancelled || closedByCleanupRef.current) return
        setWsError({
          code: "WS_CONNECTION_FAILED",
          message: "WebSocket connection failed",
        })
      })

      ws.addEventListener("message", (event) => {
        try {
          const msg = JSON.parse(event.data as string) as ServerMessage
          if (msg.type === "state") {
            setState(msg.state)
          } else if (msg.type === "presence") {
            setConnectionState(msg.connectionState)
            setDisconnectDeadlines(msg.disconnectDeadlines)
          } else if (msg.type === "error") {
            setWsError({
              code: msg.code ?? "UNKNOWN",
              message: msg.error,
            })
          }
        } catch {
          // ignore malformed messages
        }
      })
    }

    connect()

    return () => {
      cancelled = true
      closedByCleanupRef.current = true
      if (reconnectTimerRef.current !== null) {
        window.clearTimeout(reconnectTimerRef.current)
        reconnectTimerRef.current = null
      }
      wsRef.current?.close()
      wsRef.current = null
    }
  }, [matchId, retryKey, seatId])

  const sendCommand = useCallback((cmd: ActionCommand) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(cmd))
    }
  }, [])

  return {
    connectionState,
    disconnectDeadlines,
    state,
    status,
    wsError,
    sendCommand,
  }
}
